import React, { useState } from "react";
import { FiActivity } from "react-icons/fi";
import { Prescription } from "../../../types";
import { ProgressData } from "../../../services/assistant";
import {
  DiffPrescriptionItem,
  WorkoutPlanAIModalHeader,
  WorkoutPlanAIModalFooter,
  WorkoutDescriptionInput,
} from "./WorkoutPlanForm/WorkoutPlanAIModal";

interface WorkoutPlanAIModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly description: string;
  readonly professionalId: number;
  readonly userId?: number | string;
  readonly onDescriptionChange: (value: string) => void;
  readonly onAnalyze: () => void;
  readonly onApply: (prescriptions: Prescription[]) => void;
  readonly onBack: () => void;
  readonly aiLoading: boolean;
  readonly aiResult: Prescription[] | null;
  readonly originalPrescriptions: Prescription[];
  readonly progress?: ProgressData;
}

export function WorkoutPlanAIModal({
  isOpen,
  onClose,
  description,
  onDescriptionChange,
  onAnalyze,
  onApply,
  onBack,
  aiLoading,
  aiResult,
  originalPrescriptions,
  professionalId,
  userId,
  progress,
}: WorkoutPlanAIModalProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedPrescriptions, setEditedPrescriptions] = useState<
    Prescription[]
  >(aiResult || []);
  const [removedIndices, setRemovedIndices] = useState<Set<number>>(new Set());

  // Sincroniza editedPrescriptions quando aiResult muda
  React.useEffect(() => {
    if (aiResult) {
      setEditedPrescriptions(aiResult);
      setRemovedIndices(new Set());
      setEditingIndex(null);
    }
  }, [aiResult]);

  if (!isOpen) return null;

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index: number, prescription: Prescription) => {
    const newPrescriptions = [...editedPrescriptions];
    newPrescriptions[index] = prescription;
    setEditedPrescriptions(newPrescriptions);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleRemove = (index: number) => {
    setRemovedIndices((prev) => new Set(prev).add(index));
    setEditingIndex(null);
  };

  const handleApply = () => {
    const filteredPrescriptions = editedPrescriptions.filter(
      (_, index) => !removedIndices.has(index)
    );

    if (filteredPrescriptions.length === 0) {
      return;
    }

    onApply(filteredPrescriptions);
  };

  const visiblePrescriptionsWithIndex = editedPrescriptions
    .map((prescription, index) => ({ prescription, originalIndex: index }))
    .filter(({ originalIndex }) => !removedIndices.has(originalIndex));

  return (
    <div className="fixed inset-0 z-[999999999] flex items-center justify-center bg-black/60 p-0 sm:p-4 sm:backdrop-blur-sm overflow-y-auto">
      <div className="relative flex h-full w-full sm:h-[85vh] max-w-full sm:max-w-6xl flex-col overflow-hidden sm:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark sm:my-auto">
        <WorkoutPlanAIModalHeader onClose={onClose} />

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto bg-gray-50/50 p-4 sm:p-8 dark:bg-gray-900/20">
            {aiResult === null ? (
              <WorkoutDescriptionInput
                description={description}
                onDescriptionChange={onDescriptionChange}
                disabled={aiLoading}
                progress={progress}
              />
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {/* Comparação Antes/Depois */}
                {visiblePrescriptionsWithIndex.length > 0 && (
                  <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
                    <h4 className="mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      <FiActivity className="text-purple-600" size={18} />
                      Treinos que serão criados (
                      {visiblePrescriptionsWithIndex.length})
                    </h4>
                    <div className="space-y-2 sm:space-y-3">
                      {visiblePrescriptionsWithIndex.map(
                        ({ prescription, originalIndex }, displayIndex) => {
                          const prescriptionKey =
                            prescription.id ||
                            prescription.name ||
                            `prescription-${originalIndex}`;
                          const originalPrescription =
                            originalIndex >= 0 &&
                            originalIndex < originalPrescriptions.length
                              ? originalPrescriptions[originalIndex]
                              : undefined;

                          return (
                            <DiffPrescriptionItem
                              key={prescriptionKey}
                              prescription={prescription}
                              originalPrescription={originalPrescription}
                              index={displayIndex}
                              isEditing={editingIndex === originalIndex}
                              editedPrescription={
                                editedPrescriptions[originalIndex]
                              }
                              onEdit={() => handleEdit(originalIndex)}
                              onSave={(prescription) =>
                                handleSaveEdit(originalIndex, prescription)
                              }
                              onCancel={handleCancelEdit}
                              onRemove={() => handleRemove(originalIndex)}
                              professionalId={professionalId}
                              userId={userId}
                              onPrescriptionChange={(prescription) => {
                                const newPrescriptions = [
                                  ...editedPrescriptions,
                                ];
                                newPrescriptions[originalIndex] = prescription;
                                setEditedPrescriptions(newPrescriptions);
                              }}
                            />
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

                {visiblePrescriptionsWithIndex.length === 0 && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 sm:p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                    <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-300">
                      Todos os treinos foram removidos. Nenhum treino será
                      criado.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <WorkoutPlanAIModalFooter
            hasResult={aiResult !== null}
            aiLoading={aiLoading}
            description={description}
            visiblePrescriptionsCount={visiblePrescriptionsWithIndex.length}
            onClose={onClose}
            onAnalyze={onAnalyze}
            onBack={onBack}
            onApply={handleApply}
          />
        </div>
      </div>
    </div>
  );
}
